// ./hooks/useStudent.js

import { useContext, useCallback } from 'react';
import { GlobalContext } from '../context/provider';
import studentAPI from '../api/studentAPI';

const useStudent = () => {
  const { 
    studentState,
    studentDispatch
 } = useContext(GlobalContext);
  
  // ============================================================================
  // STUDENT PROFILE
  // ============================================================================

  const getStudentByLoginId = useCallback(async (loginId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.getStudentByLoginId(loginId);
      
      if (response.success) {
        studentDispatch({ type: 'SET_STUDENT_DATA', payload: response.data });
        
        // Also set enrolled courses separately for easy access
        if (response.data.courses) {
          studentDispatch({ type: 'SET_ENROLLED_COURSES', payload: response.data.courses });
        }
      }
      
      // console.log("student hook data: ", response);
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      console.log("student hook error: ", error.message);
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  const createStudent = useCallback(async (loginId, newData) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.createStudent(loginId, newData);
      
      if (response.success) {
        studentDispatch({ type: 'SET_STUDENT_DATA', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  const updateStudent = useCallback(async (loginId, updateData) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.updateStudent(loginId, updateData);
      
      if (response.success) {
        studentDispatch({ type: 'UPDATE_STUDENT_PROFILE', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  const getAllStudents = useCallback(async (accountStatus) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.getAllStudents(accountStatus);
      
      if (response.success) {
        studentDispatch({ type: 'LOAD_STUDENTs', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  // ============================================================================
  // COURSE ENROLLMENT
  // ============================================================================

  const enrollCourse = useCallback(async (loginId, courseId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.enrollCourse(loginId, courseId);
      
      if (response.success) {
        // Update student data with new enrollment
        studentDispatch({ type: 'ENROLL_COURSE', payload: response.data });
        
        // Refresh student data to get updated courses
        await getStudentByLoginId(loginId);
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch, getStudentByLoginId]);

  const unenrollCourse = useCallback(async (loginId, courseId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.unenrollCourse(loginId, courseId);
      
      if (response.success) {
        studentDispatch({ type: 'UNENROLL_COURSE', payload: courseId });
        
        // Refresh student data
        await getStudentByLoginId(loginId);
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch, getStudentByLoginId]);

  // ============================================================================
  // LAB SUBMISSIONS
  // ============================================================================

  const submitLabAnswers = useCallback(async (loginId, labData) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.submitLabAnswers(loginId, labData);
      
      if (response.success) {
        studentDispatch({ type: 'ADD_LAB_SUBMISSION', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  const getLabProgress = useCallback(async (loginId, courseId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.getLabProgress(loginId, courseId);
      
      if (response.success) {
        studentDispatch({ type: 'SET_LAB_PROGRESS', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  // ============================================================================
  // QUIZ SUBMISSIONS
  // ============================================================================

  const submitQuizAnswers = useCallback(async (loginId, quizData) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.submitQuizAnswers(loginId, quizData);
      
      if (response.success) {
        studentDispatch({ type: 'ADD_QUIZ_SUBMISSION', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  const getQuizProgress = useCallback(async (loginId, courseId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.getQuizProgress(loginId, courseId);
      
      if (response.success) {
        studentDispatch({ type: 'SET_QUIZ_PROGRESS', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  // ============================================================================
  // EXAM SUBMISSIONS
  // ============================================================================

  const submitExamAnswers = useCallback(async (loginId, examData) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.submitExamAnswers(loginId, examData);
      
      if (response.success) {
        studentDispatch({ type: 'ADD_EXAM_SUBMISSION', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  const getExamProgress = useCallback(async (loginId, courseId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.getExamProgress(loginId, courseId);
      
      if (response.success) {
        studentDispatch({ type: 'SET_EXAM_PROGRESS', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  // ============================================================================
  // CAPSTONE SUBMISSIONS
  // ============================================================================

  const submitCapstone = useCallback(async (loginId, capstoneData) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.submitCapstone(loginId, capstoneData);
      
      if (response.success) {
        studentDispatch({ type: 'ADD_CAPSTONE_SUBMISSION', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  const getCapstoneProgress = useCallback(async (loginId, courseId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.getCapstoneProgress(loginId, courseId);
      
      if (response.success) {
        studentDispatch({ type: 'SET_CAPSTONE_PROGRESS', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  // ============================================================================
  // GRADES
  // ============================================================================

  const getCourseGrade = useCallback(async (loginId, courseId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.getCourseGrade(loginId, courseId);
      
      if (response.success) {
        studentDispatch({ 
          type: 'SET_COURSE_GRADE', 
          payload: { courseId, grade: response.data } 
        });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  const getAllGrades = useCallback(async (loginId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.getAllGrades(loginId);
      
      if (response.success) {
        studentDispatch({ type: 'SET_ALL_GRADES', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  const calculateGrade = useCallback(async (loginId, courseId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.calculateGrade(loginId, courseId);
      
      if (response.success) {
        studentDispatch({ type: 'CALCULATE_GRADE', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  // ============================================================================
  // PROGRESS
  // ============================================================================

  const getCourseProgress = useCallback(async (loginId, courseId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.getCourseProgress(loginId, courseId);
      
      if (response.success) {
        studentDispatch({ 
          type: 'SET_COURSE_PROGRESS', 
          payload: { courseId, progress: response.data }
        });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  // ============================================================================
  // SHOPPING CART
  // ============================================================================

  const addToCart = useCallback(async (loginId, cartItem) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.addToCart(loginId, cartItem);
      
      if (response.success) {
        studentDispatch({ type: 'ADD_TO_CART', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  const getCart = useCallback(async (loginId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.getCart(loginId);
      
      if (response.success) {
        studentDispatch({ type: 'SET_CART', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  const removeFromCart = useCallback(async (loginId, courseId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.removeFromCart(loginId, courseId);
      
      if (response.success) {
        studentDispatch({ type: 'REMOVE_FROM_CART', payload: courseId });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  // ============================================================================
  // STRIPE PAYMENT METHODS
  // ============================================================================

  const createStripeCheckout = useCallback(async (loginId, checkoutData) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.createStripeCheckout(loginId, checkoutData);
      
      if (response.success) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    } finally {
      studentDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [studentDispatch]);

  const createSubscription = useCallback(async (loginId, subscriptionData) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.createSubscription(loginId, subscriptionData);
      
      if (response.success) {
        studentDispatch({ type: 'CREATE_SUBSCRIPTION', payload: response.data });
        
        // Refresh student data
        await getStudentByLoginId(loginId);
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    } finally {
      studentDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [studentDispatch, getStudentByLoginId]);

  const cancelSubscription = useCallback(async (loginId, subscriptionId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.cancelSubscription(loginId, subscriptionId);
      
      if (response.success) {
        studentDispatch({ type: 'CANCEL_SUBSCRIPTION', payload: subscriptionId });
        
        // Refresh student data
        await getStudentByLoginId(loginId);
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    } finally {
      studentDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [studentDispatch, getStudentByLoginId]);

  const getSubscriptions = useCallback(async (loginId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.getSubscriptions(loginId);
      
      if (response.success) {
        studentDispatch({ type: 'SET_SUBSCRIPTIONS', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    } finally {
      studentDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [studentDispatch]);

  // const validateCoupon = useCallback(async (couponCode) => {
  //   studentDispatch({ type: 'SET_LOADING', payload: true });
  //   try {
  //     const response = await studentAPI.validateCoupon(couponCode);
  //     return response;
  //   } catch (error) {
  //     studentDispatch({ type: 'SET_ERROR', payload: error.message });
  //     return { success: false, message: error.message };
  //   } finally {
  //     studentDispatch({ type: 'SET_LOADING', payload: false });
  //   }
  // }, [studentDispatch]);

  // const applyCoupon = useCallback(async (loginId, couponCode) => {
  //   studentDispatch({ type: 'SET_LOADING', payload: true });
  //   try {
  //     const response = await studentAPI.applyCoupon(loginId, couponCode);
      
  //     if (response.success) {
  //       studentDispatch({ type: 'APPLY_COUPON', payload: response.data });
  //     }
      
  //     return response;
  //   } catch (error) {
  //     studentDispatch({ type: 'SET_ERROR', payload: error.message });
  //     return { success: false, message: error.message };
  //   } finally {
  //     studentDispatch({ type: 'SET_LOADING', payload: false });
  //   }
  // }, [studentDispatch]);

  const refundPayment = useCallback(async (loginId, paymentId, reason) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.refundPayment(loginId, paymentId, reason);
      
      if (response.success) {
        // Refresh student data to update payment history
        await getStudentByLoginId(loginId);
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    } finally {
      studentDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [studentDispatch, getStudentByLoginId]);

  const refundToCredit = useCallback(async (loginId, paymentId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.refundToCredit(loginId, paymentId);
      
      if (response.success) {
        studentDispatch({ type: 'UPDATE_CREDIT_BALANCE', payload: response.data.newBalance });
        
        // Refresh student data
        await getStudentByLoginId(loginId);
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    } finally {
      studentDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [studentDispatch, getStudentByLoginId]);

  const processRefund = useCallback(async (loginId, paymentId, action) => {
    try {
        const response = await studentAPI.processRefund(loginId, paymentId, action);
        return response;
    } catch (error) {
        return { success: false, message: error.message };
    }
  }, []);

  const getPaymentMethods = useCallback(async (loginId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.getPaymentMethods(loginId);
      
      if (response.success) {
        studentDispatch({ type: 'SET_PAYMENT_METHODS', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    } finally {
      studentDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [studentDispatch]);

  const addPaymentMethod = useCallback(async (loginId, paymentMethodId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.addPaymentMethod(loginId, paymentMethodId);
      
      if (response.success) {
        // Refresh payment methods
        await getPaymentMethods(loginId);
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    } finally {
      studentDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [studentDispatch, getPaymentMethods]);

  const processPayment = useCallback(async (loginId, paymentData) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.processPayment(loginId, paymentData);
      
      if (response.success) {
        studentDispatch({ type: 'PROCESS_PAYMENT', payload: response.data });
        studentDispatch({ type: 'CLEAR_CART' });
        
        // Refresh student data to show new enrollments
        await getStudentByLoginId(loginId);
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch, getStudentByLoginId]);

  const getPaymentHistory = useCallback(async (loginId) => {
    studentDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await studentAPI.getPaymentHistory(loginId);
      
      if (response.success) {
        studentDispatch({ type: 'SET_PAYMENT_HISTORY', payload: response.data });
      }
      
      return response;
    } catch (error) {
      studentDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    }
  }, [studentDispatch]);

  const getSubscriptionInvoiceUrl = useCallback(async (loginId, subscriptionId) => {
      try {
          const response = await studentAPI.getSubscriptionInvoiceUrl(loginId, subscriptionId);
          return response;
      } catch (error) {
          return { success: false, message: error.message };
      }
  }, []);


  // ============================================================================
  // UTILITY
  // ============================================================================

  const clearStudentData = useCallback(() => {
    studentDispatch({ type: 'CLEAR_STUDENT_DATA' });
  }, [studentDispatch]);

  return {
    // State
    studentData: studentState.studentData,
    enrolledCourses: studentState.enrolledCourses,
    completedCourses: studentState.completedCourses,
    labSubmissions: studentState.labSubmissions,
    quizSubmissions: studentState.quizSubmissions,
    examSubmissions: studentState.examSubmissions,
    capstoneSubmissions: studentState.capstoneSubmissions,
    cart: studentState.cart,
    paymentHistory: studentState.paymentHistory,
    isLoading: studentState.isLoading,
    error: studentState.error,

    // Methods
    getStudentByLoginId,
    createStudent,
    updateStudent,
    getAllStudents,
    enrollCourse,
    unenrollCourse,
    submitLabAnswers,
    getLabProgress,
    submitQuizAnswers,
    getQuizProgress,
    submitExamAnswers,
    getExamProgress,
    submitCapstone,
    getCapstoneProgress,
    getCourseGrade,
    getAllGrades,
    calculateGrade,
    getCourseProgress,
    addToCart,
    getCart,
    removeFromCart,
    clearStudentData,
    // Stripe payments
    createStripeCheckout,
    createSubscription,
    cancelSubscription,
    getSubscriptions,
    // validateCoupon,
    // applyCoupon,
    refundPayment,
    refundToCredit,
    processRefund,
    getPaymentMethods,
    addPaymentMethod,
    processPayment,
    getPaymentHistory,
    getSubscriptionInvoiceUrl,
  };
};

export default useStudent;